import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  HardDrive,
  Loader2,
  TrendingUp,
  Database,
  ArrowDownToLine,
  ArrowUpFromLine,
  Info,
  ShieldAlert,
  Server,
  FileText,
  Zap,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import baseapi from '../../../services/baseapi';
import { ItemsPerPageDropdown } from '../../../components/ItemsPerPageDropdown ';

export interface ClickhouseFileIOViewProps {
  activeCluster: string;
  timeRange?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

interface DatabaseDistributionItem {
  database: string;
  size_bytes: number;
  scope?: string;
}

export interface HighestIOFileItem {
  file_name: string;
  database: string;
  peak_kbps: number;
}

export interface DatabaseFileItem {
  file_name: string;
  database: string;
  type: string;
  current_kbps: number;
  average_kbps: number;
  peak_kbps: number;
  samples: number;
  total_samples: number;
  status: string;
  read_latency_ms?: number | null;
  write_latency_ms?: number | null;
  used_mb?: number | null;
  max_size_mb?: number | null;
  is_percent_growth?: boolean | null;
  growth_config?: string | null;
}

interface ClickhouseFileIOSummary {
  source?: string;
  available?: boolean;
  reason?: string | null;
  current_read_kbps?: number;
  average_read_kbps?: number;
  peak_read_kbps?: number;
  current_write_kbps?: number;
  average_write_kbps?: number;
  peak_write_kbps?: number;
  samples?: number;
  total_samples?: number;
  reset_detected?: boolean;
  data_status?: string;
  latest_sample_age_sec?: number;
  database_distribution?: DatabaseDistributionItem[];
  active_files?: number | null;
  active_files_reason?: string | null;
  highest_io_files?: HighestIOFileItem[];
  files?: DatabaseFileItem[];
}

interface ClickhouseFileIOData {
  labels: string[];
  series: {
    read_kbps?: (number | null)[];
    write_kbps?: (number | null)[];
  };
  summary?: ClickhouseFileIOSummary;
  highest_io_files?: HighestIOFileItem[];
  files?: DatabaseFileItem[];
}

const formatKBps = (kbps: number | undefined | null) => {
  if (kbps === undefined || kbps === null || isNaN(kbps)) return '0.00 KB/s';
  const val = Number(kbps);
  if (val >= 1024 * 1024) {
    return `${(val / (1024 * 1024)).toFixed(2)} GB/s`;
  }
  if (val >= 1024) {
    return `${(val / 1024).toFixed(2)} MB/s`;
  }
  return `${val.toFixed(2)} KB/s`;
};

const formatBytes = (bytes: number | undefined | null) => {
  if (bytes === undefined || bytes === null || isNaN(bytes)) return '0 B';
  const b = Number(bytes);
  if (b === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(b) / Math.log(k));
  const safeI = Math.min(i, sizes.length - 1);
  return `${parseFloat((b / Math.pow(k, safeI)).toFixed(2))} ${sizes[safeI]}`;
};

const formatAgeSec = (seconds: number | undefined | null) => {
  if (seconds === undefined || seconds === null || isNaN(seconds)) return null;
  const sec = Math.round(Number(seconds));
  if (sec < 60) return `${sec}s ago`;
  const mins = Math.floor(sec / 60);
  const remSec = sec % 60;
  if (mins < 60) return `${mins}m ${remSec}s ago`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  return `${hours}h ${remMins}m ago`;
};

const formatLatency = (read: number | null | undefined, write: number | null | undefined) => {
  if ((read === null || read === undefined) && (write === null || write === undefined)) {
    return '—';
  }
  const r = read !== null && read !== undefined ? `${Number(read).toFixed(2)}ms` : '—';
  const w = write !== null && write !== undefined ? `${Number(write).toFixed(2)}ms` : '—';
  return `R: ${r} / W: ${w}`;
};

const formatFileSizeMB = (used: number | null | undefined, max: number | null | undefined) => {
  if ((used === null || used === undefined) && (max === null || max === undefined)) {
    return '—';
  }
  const u = used !== null && used !== undefined ? `${Number(used).toFixed(1)} MB` : '—';
  const m = max !== null && max !== undefined ? `${Number(max).toFixed(1)} MB` : 'Unlimited';
  return `${u} / ${m}`;
};

const getStatusBadge = (status: string) => {
  const st = (status || 'Normal').toLowerCase();
  if (st === 'high') {
    return (
      <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-red-50 dark:bg-red-500/20 text-red-700 dark:text-red-400 border border-red-300 dark:border-red-500/30">
        High
      </span>
    );
  }
  if (st === 'elevated') {
    return (
      <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-50 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-500/30">
        Elevated
      </span>
    );
  }
  return (
    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-50 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-500/30">
      Normal
    </span>
  );
};

const getTypeBadge = (type: string) => {
  const t = (type || 'DATA').toUpperCase();
  if (t === 'LOG') {
    return (
      <span className="inline-block px-2 py-0.5 rounded text-[9px] font-bold uppercase bg-purple-50 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-500/30">
        LOG
      </span>
    );
  }
  return (
    <span className="inline-block px-2 py-0.5 rounded text-[9px] font-bold uppercase bg-blue-50 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30">
      DATA
    </span>
  );
};

// const ScrollableLegend = (props: any) => {
//   const { payload, hoveredSeries, onHoverSeries } = props;
//   if (!payload || payload.length === 0) return null;
//   return (
//     <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 max-h-16 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
//       {payload.map((entry: any, index: number) => {
//         const seriesKey = entry.dataKey || entry.value;
//         const isHovered = hoveredSeries === seriesKey;
//         const isDimmed = hoveredSeries !== null && !isHovered;
//         return (
//           <div
//             key={`legend-item-${index}`}
//             onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
//             onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
//             className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${
//               isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
//             }`}
//           >
//             <span
//               className="w-2.5 h-2.5 rounded-full inline-block shrink-0 shadow-sm"
//               style={{ backgroundColor: entry.color }}
//             />
//             <span
//               className="text-gray-700 dark:text-gray-200 text-xs truncate max-w-[160px] font-mono font-medium"
//               title={entry.value}
//             >
//               {entry.value}
//             </span>
//           </div>
//         );
//       })}
//     </div>
//   );
// };

// const ChartTooltip = ({ active, payload, label }: any) => {
//   if (active && payload && payload.length) {
//     return (
//       <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border border-gray-200 dark:border-gray-700/80 rounded-lg p-2.5 shadow-xl text-[11px] text-gray-900 dark:text-gray-100 flex flex-col gap-1.5 min-w-[140px] z-50">
//         {label !== undefined && label !== null && label !== '' && (
//           <div className="text-gray-700 dark:text-gray-300 font-bold border-b border-gray-100 dark:border-gray-800 pb-1 mb-0.5 text-[11px] truncate">
//             {label}
//           </div>
//         )}
//         {payload.map((entry: any, index: number) => (
//           <div key={index} className="flex justify-between items-center gap-4">
//             <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-medium">
//               <span
//                 className="inline-block w-2 h-2 rounded-full shrink-0"
//                 style={{ backgroundColor: entry.color || entry.stroke || entry.fill || '#10b981' }}
//               />
//               {entry.name || entry.dataKey}
//             </span>
//             <span className="font-bold text-gray-900 dark:text-white font-mono">
//               {typeof entry.value === 'number' ? formatKBps(entry.value) : (entry.value ?? '-')}
//             </span>
//           </div>
//         ))}
//       </div>
//     );
//   }
//   return null;
// };

export const ClickhouseFileIOView: React.FC<ClickhouseFileIOViewProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
  dbType = 'clickhouse',
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [fileIOData, setFileIOData] = useState<ClickhouseFileIOData | null>(null);
  // const [metricFilter, setMetricFilter] = useState<'all' | 'read' | 'write'>('all');
  // const [hoveredSeries, setHoveredSeries] = useState<string | null>(null);

  // Pagination & search for files table
  const [tableSearchQuery, setTableSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const minutes = parseInt(timeRange || '60', 10);
    const params: any = {
      cluster_id: activeCluster,
      minutes,
    };
    if (clientId) {
      params.client_id = clientId;
    }

    try {
      const res = await baseapi.get('/db_monitor_dashboard/graph/clickhouse/file_io', { params });
      setFileIOData(res.data?.data || res.data);
    } catch (err) {
      console.warn('ClickhouseFileIOView fetch error:', err);
      setFileIOData(null);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const labels: string[] = fileIOData?.labels || [];
  // const readSeries: (number | null)[] = fileIOData?.series?.read_kbps || [];
  // const writeSeries: (number | null)[] = fileIOData?.series?.write_kbps || [];
  const summary: ClickhouseFileIOSummary | undefined = fileIOData?.summary;

  const highestIoFiles: HighestIOFileItem[] = useMemo(() => {
    const list = summary?.highest_io_files || fileIOData?.highest_io_files;
    return Array.isArray(list) ? list : [];
  }, [summary, fileIOData]);

  const rawFiles: DatabaseFileItem[] = useMemo(() => {
    const list = summary?.files || fileIOData?.files;
    return Array.isArray(list) ? list : [];
  }, [summary, fileIOData]);

  const filteredFiles = useMemo(() => {
    if (!tableSearchQuery.trim()) return rawFiles;
    const q = tableSearchQuery.toLowerCase().trim();
    return rawFiles.filter(
      (f) =>
        f.file_name?.toLowerCase().includes(q) ||
        f.database?.toLowerCase().includes(q) ||
        f.type?.toLowerCase().includes(q) ||
        f.status?.toLowerCase().includes(q)
    );
  }, [rawFiles, tableSearchQuery]);

  const totalPages = Math.ceil(filteredFiles.length / itemsPerPage) || 1;
  const paginatedFiles = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredFiles.slice(start, start + itemsPerPage);
  }, [filteredFiles, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [tableSearchQuery, itemsPerPage]);

  const formatTimeLabels = (lbls: string[]) => {
    if (!lbls || lbls.length === 0) return [];
    const minutes = parseInt(timeRange || '60', 10);
    const seen: { [key: string]: number } = {};
    return lbls.map((lbl) => {
      const d = new Date(lbl);
      let base = isNaN(d.getTime())
        ? lbl
        : minutes > 1440
          ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (seen[base] !== undefined) {
        seen[base] += 1;
        base = `${base}${' '.repeat(seen[base])}`;
      } else {
        seen[base] = 0;
      }
      return base;
    });
  };

  const timeLabels = useMemo(() => formatTimeLabels(labels), [labels, timeRange]);

  // const formattedChartData = useMemo(() => {
  //   if (!labels || labels.length === 0) return [];
  //   return labels.map((_, idx) => {
  //     const r = readSeries[idx];
  //     const w = writeSeries[idx];
  //     const readVal = r !== undefined && r !== null ? Number(Number(r).toFixed(2)) : 0;
  //     const writeVal = w !== undefined && w !== null ? Number(Number(w).toFixed(2)) : 0;

  //     return {
  //       time: timeLabels[idx],
  //       'Read (KB/s)': readVal,
  //       'Write (KB/s)': writeVal,
  //     };
  //   });
  // }, [labels, readSeries, writeSeries, timeLabels]);

  // const hasData = (arr: any[]) => {
  //   return arr && arr.length > 0;
  // };

  // const getOpacity = (hovered: string | null, key: string) => {
  //   if (hovered === null) return 1;
  //   return hovered === key ? 1 : 0.15;
  // };

  const currentTotalKBps =
    (summary?.current_read_kbps ?? 0) + (summary?.current_write_kbps ?? 0);
  const avgTotalKBps =
    (summary?.average_read_kbps ?? 0) + (summary?.average_write_kbps ?? 0);
  const peakTotalKBps =
    (summary?.peak_read_kbps ?? 0) + (summary?.peak_write_kbps ?? 0);

  // Maximum size for database distribution progress bar calculations
  const totalDbStorageBytes = useMemo(() => {
    if (!summary?.database_distribution || summary.database_distribution.length === 0) return 0;
    return summary.database_distribution.reduce((acc, curr) => acc + (Number(curr.size_bytes) || 0), 0);
  }, [summary]);

  const maxDbSizeBytes = useMemo(() => {
    if (!summary?.database_distribution || summary.database_distribution.length === 0) return 1;
    const max = Math.max(...summary.database_distribution.map((d) => Number(d.size_bytes) || 0));
    return max > 0 ? max : 1;
  }, [summary]);

  if (loading) {
    return (
      <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl min-h-[350px]">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  const isStale = (summary?.data_status || '').toLowerCase() === 'stale';
  const hasActiveFiles = summary?.active_files !== null && summary?.active_files !== undefined;

  return (
    <div className="space-y-4 relative">
      {/* Top Banner Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-md">
            <HardDrive className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">File I/O</h2>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30">
                {(dbType || 'ClickHouse').toUpperCase()}
              </span>
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              Operating system read & write disk I/O throughput & storage activity
            </p>
          </div>
        </div>

        {/* Header Right: Status Badge & Sample Info */}
        <div className="flex items-center gap-3">
          <div className="text-[11px] text-gray-500 dark:text-gray-400 hidden sm:block">
            <span className="font-mono text-gray-700 dark:text-gray-200 font-semibold">{labels.length}</span> samples
            {labels.length > 0 && timeLabels.length > 0 && (
              <>
                {' '}• Last sample: <span className="font-mono text-gray-700 dark:text-gray-200">{timeLabels[timeLabels.length - 1]}</span>
              </>
            )}
          </div>

          {labels.length > 0 ? (
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border shadow-sm ${
                isStale
                  ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-500/30'
                  : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-500/30'
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full ${
                  isStale ? 'bg-amber-500' : 'bg-emerald-500 animate-pulse'
                }`}
              />
              {summary?.data_status
                ? `${summary.data_status}${summary.latest_sample_age_sec ? ` (${formatAgeSec(summary.latest_sample_age_sec)})` : ''}`
                : 'Monitoring Active'}
            </span>
          ) : (
            <span className="text-gray-700 text-xs dark:text-gray-200 font-semibold">No Samples</span>
          )}
        </div>
      </div>

      {/* Availability warning banner if available is false */}
      {summary?.available === false && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800/60 rounded-xl text-red-800 dark:text-red-300 text-xs">
          <ShieldAlert className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0" />
          <span>{summary.reason || 'ClickHouse File I/O metrics are currently unavailable.'}</span>
        </div>
      )}

      {/* Top KPI Summary Cards */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${hasActiveFiles ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-3`}>
        {/* CURRENT READ I/O */}
        <div className="bg-emerald-50/70 dark:bg-gray-900 border border-emerald-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-md flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">
                CURRENT READ I/O
              </span>
              <ArrowDownToLine className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="text-xl font-bold font-mono text-emerald-600 dark:text-emerald-400">
              {formatKBps(summary?.current_read_kbps ?? 0)}
            </div>
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-2 flex items-center justify-between">
            <span>Avg: {formatKBps(summary?.average_read_kbps ?? 0)}</span>
            <span>Peak: {formatKBps(summary?.peak_read_kbps ?? 0)}</span>
          </div>
        </div>

        {/* CURRENT WRITE I/O */}
        <div className="bg-amber-50/70 dark:bg-gray-900 border border-amber-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-md flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider">
                CURRENT WRITE I/O
              </span>
              <ArrowUpFromLine className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="text-xl font-bold font-mono text-amber-600 dark:text-amber-400">
              {formatKBps(summary?.current_write_kbps ?? 0)}
            </div>
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-2 flex items-center justify-between">
            <span>Avg: {formatKBps(summary?.average_write_kbps ?? 0)}</span>
            <span>Peak: {formatKBps(summary?.peak_write_kbps ?? 0)}</span>
          </div>
        </div>

        {/* TOTAL I/O THROUGHPUT */}
        <div className="bg-indigo-50/70 dark:bg-gray-900 border border-indigo-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-md flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-indigo-700 dark:text-indigo-400 uppercase tracking-wider">
                TOTAL I/O THROUGHPUT
              </span>
              <TrendingUp className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div className="text-xl font-bold font-mono text-indigo-600 dark:text-indigo-400">
              {formatKBps(currentTotalKBps)}
            </div>
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-2 flex items-center justify-between">
            <span>Avg: {formatKBps(avgTotalKBps)}</span>
            <span>Peak: {formatKBps(peakTotalKBps)}</span>
          </div>
        </div>

        {/* ACTIVE FILES (Shown if available) */}
        {hasActiveFiles && (
          <div className="bg-blue-50/70 dark:bg-gray-900 border border-blue-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-md flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-bold text-blue-700 dark:text-blue-400 uppercase tracking-wider">
                  ACTIVE FILES
                </span>
                <FileText className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="text-xl font-bold font-mono text-blue-600 dark:text-blue-400">
                {summary?.active_files?.toLocaleString()}
              </div>
            </div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-2 truncate">
              Monitored active disk files
            </div>
          </div>
        )}
      </div>

      {/* Main Full-Width Chart: ClickHouse File I/O Throughput */}
      {/* <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2.5">
          <div>
            <div className="flex items-center gap-1.5">
              <BarChart2 className="w-4 h-4 text-amber-500 dark:text-amber-400" />
              <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                ClickHouse File I/O Throughput
              </h4>
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
              Real-time operating system read and write rate over time
            </p>
          </div>

  
          <div className="flex items-center bg-gray-100 dark:bg-gray-800 p-0.5 rounded-lg border border-gray-200 dark:border-gray-700 shrink-0 self-start sm:self-auto">
            <button
              onClick={() => setMetricFilter('all')}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer ${
                metricFilter === 'all'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-bold shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Both
            </button>
            <button
              onClick={() => setMetricFilter('read')}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer ${
                metricFilter === 'read'
                  ? 'bg-emerald-600 text-white font-bold shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Read Only
            </button>
            <button
              onClick={() => setMetricFilter('write')}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer ${
                metricFilter === 'write'
                  ? 'bg-amber-600 text-white font-bold shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Write Only
            </button>
          </div>
        </div>

        <div className="h-72 w-full flex items-center justify-center">
          {hasData(formattedChartData) ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={formattedChartData}>
                <defs>
                  <linearGradient id="chReadGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                  </linearGradient>
                  <linearGradient id="chWriteGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" opacity={0.2} />
                <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                <YAxis
                  stroke="#9ca3af"
                  fontSize={10}
                  tickLine={false}
                  tickCount={5}
                  tickFormatter={(val: number) => {
                    if (val >= 1024) return `${(val / 1024).toFixed(1)} MB/s`;
                    return `${val} KB/s`;
                  }}
                >
                  <Label
                    value="Throughput (KB/s)"
                    angle={-90}
                    position="insideLeft"
                    offset={10}
                    fill="#9ca3af"
                    fontSize={10}
                  />
                </YAxis>
                <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                <Legend
                  content={
                    <ScrollableLegend
                      hoveredSeries={hoveredSeries}
                      onHoverSeries={setHoveredSeries}
                    />
                  }
                />

                {(metricFilter === 'all' || metricFilter === 'read') && (
                  <Area
                    type="monotone"
                    dataKey="Read (KB/s)"
                    name="Read (KB/s)"
                    stroke="#10b981"
                    strokeWidth={hoveredSeries === 'Read (KB/s)' ? 2.5 : 2}
                    fill="url(#chReadGrad)"
                    fillOpacity={getOpacity(hoveredSeries, 'Read (KB/s)')}
                    strokeOpacity={getOpacity(hoveredSeries, 'Read (KB/s)')}
                    dot={false}
                  />
                )}

                {(metricFilter === 'all' || metricFilter === 'write') && (
                  <Area
                    type="monotone"
                    dataKey="Write (KB/s)"
                    name="Write (KB/s)"
                    stroke="#f59e0b"
                    strokeWidth={hoveredSeries === 'Write (KB/s)' ? 2.5 : 2}
                    fill="url(#chWriteGrad)"
                    fillOpacity={getOpacity(hoveredSeries, 'Write (KB/s)')}
                    strokeOpacity={getOpacity(hoveredSeries, 'Write (KB/s)')}
                    dot={false}
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-gray-400 dark:text-gray-500 text-xs italic">
              No File I/O chart metrics recorded for this cluster
            </div>
          )}
        </div>
      </div> */}

      {/* Summary Cards: Database Storage Distribution & ClickHouse Diagnostics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Database Storage Distribution */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700/50 pb-2 mb-3">
              <div className="flex items-center gap-1.5">
                <Database className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                  Database Storage Distribution
                </h4>
              </div>
              {totalDbStorageBytes > 0 && (
                <span className="text-[11px] font-mono font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 px-2 py-0.5 rounded-full border border-blue-200 dark:border-blue-500/30">
                  Total: {formatBytes(totalDbStorageBytes)}
                </span>
              )}
            </div>

            <div className="space-y-3 max-h-72 overflow-y-auto custom-scroll pr-1">
              {summary?.database_distribution && summary.database_distribution.length > 0 ? (
                summary.database_distribution.map((d: DatabaseDistributionItem, idx: number) => {
                  const bytes = Number(d.size_bytes) || 0;
                  const pct = Math.min(100, Math.max(2, (bytes / maxDbSizeBytes) * 100));
                  return (
                    <div key={idx} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-800 dark:text-gray-200">
                            {d.database}
                          </span>
                          {d.scope && (
                            <span className="px-1.5 py-0.2 rounded text-[9px] font-medium uppercase bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                              {d.scope}
                            </span>
                          )}
                        </div>
                        <span className="font-mono text-gray-700 dark:text-gray-300 text-[11px] font-bold">
                          {formatBytes(bytes)}
                        </span>
                      </div>
                      <div className="h-2 w-full bg-gray-100 dark:bg-gray-900 rounded-full overflow-hidden border border-gray-200 dark:border-gray-800">
                        <div
                          className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-300"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-xs text-gray-400 dark:text-gray-500 italic py-6 text-center">
                  No database distribution metrics available
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Telemetry & Diagnostics Summary */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700/50 pb-2 mb-3">
              <div className="flex items-center gap-1.5">
                <Server className="w-4 h-4 text-purple-500 dark:text-purple-400" />
                <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                  Engine & Telemetry Summary
                </h4>
              </div>
            </div>

            <div className="space-y-2.5 text-xs">
              <div className="flex items-center justify-between py-1 border-b border-gray-100 dark:border-gray-800/60">
                <span className="text-gray-500 dark:text-gray-400">Metric Source</span>
                <span className="font-mono text-gray-800 dark:text-gray-200 font-medium text-[11px] bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-700">
                  {summary?.source || 'ch_events.OSReadBytes/OSWriteBytes'}
                </span>
              </div>

              <div className="flex items-center justify-between py-1 border-b border-gray-100 dark:border-gray-800/60">
                <span className="text-gray-500 dark:text-gray-400">Data Freshness</span>
                <span className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                  <span
                    className={`w-2 h-2 rounded-full ${
                      isStale ? 'bg-amber-500' : 'bg-emerald-500'
                    }`}
                  />
                  {summary?.data_status || 'Active'}
                  {summary?.latest_sample_age_sec !== undefined && summary?.latest_sample_age_sec !== null && (
                    <span className="text-gray-400 dark:text-gray-500 text-[11px] font-normal">
                      ({formatAgeSec(summary.latest_sample_age_sec)})
                    </span>
                  )}
                </span>
              </div>

              <div className="flex items-center justify-between py-1 border-b border-gray-100 dark:border-gray-800/60">
                <span className="text-gray-500 dark:text-gray-400">Sample Count</span>
                <span className="font-mono text-gray-800 dark:text-gray-200 font-medium">
                  {summary?.samples ?? labels.length} / {summary?.total_samples ?? labels.length}
                </span>
              </div>

              <div className="flex items-center justify-between py-1 border-b border-gray-100 dark:border-gray-800/60">
                <span className="text-gray-500 dark:text-gray-400">Reset Detected</span>
                <span className="font-semibold text-gray-800 dark:text-gray-200">
                  {summary?.reset_detected ? (
                    <span className="text-amber-600 dark:text-amber-400">Yes</span>
                  ) : (
                    <span className="text-emerald-600 dark:text-emerald-400">No</span>
                  )}
                </span>
              </div>

              {/* Per-file note / reason */}
              {summary?.active_files_reason && (
                <div className="mt-3 p-2.5 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-[11px] text-gray-600 dark:text-gray-400 flex items-start gap-2">
                  <Info className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 shrink-0 mt-0.5" />
                  <span className="leading-relaxed">
                    <strong className="text-gray-700 dark:text-gray-300">File-level I/O Note: </strong>
                    {summary.active_files_reason}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Highest I/O Files Table Card */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-700/50 pb-2.5">
          <div className="flex items-center gap-1.5">
            <Zap className="w-4 h-4 text-orange-500 dark:text-orange-400" />
            <div>
              <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                Highest I/O Files
              </h4>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                Top disk I/O consuming database files by peak throughput
              </p>
            </div>
          </div>
          <span className="text-xs font-mono text-gray-500 dark:text-gray-400 font-medium">
            {highestIoFiles.length} {highestIoFiles.length === 1 ? 'file' : 'files'}
          </span>
        </div>

        <div className="overflow-x-auto overflow-y-auto custom-scroll border border-gray-200 dark:border-gray-700/50 rounded-lg max-h-72">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-gray-200 dark:border-gray-700/80 bg-gray-50 dark:bg-gray-900 shadow-sm text-[11px] text-gray-600 dark:text-gray-400 uppercase tracking-wider select-none">
                <th className="py-2.5 px-3.5 bg-gray-50 dark:bg-gray-900 w-16">#</th>
                <th className="py-2.5 px-3.5 bg-gray-50 dark:bg-gray-900">File Name</th>
                <th className="py-2.5 px-3.5 bg-gray-50 dark:bg-gray-900">Database</th>
                <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">Peak I/O</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/30 text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-900/40">
              {highestIoFiles.length > 0 ? (
                highestIoFiles.map((hf, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
                    <td className="py-2.5 px-3.5 font-mono text-gray-400 text-xs">{idx + 1}</td>
                    <td className="py-2.5 px-3.5 font-bold font-mono text-gray-900 dark:text-gray-100">
                      {hf.file_name}
                    </td>
                    <td className="py-2.5 px-3.5 text-gray-700 dark:text-gray-300">
                      {hf.database || 'System / Engine'}
                    </td>
                    <td className="py-2.5 px-3.5 text-right font-mono font-bold text-orange-600 dark:text-orange-400">
                      {formatKBps(hf.peak_kbps)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-gray-400 dark:text-gray-500 italic">
                    No highest I/O files telemetry recorded.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Database Files Table with Search & Pagination */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-700/50 pb-2.5">
          <div className="flex items-center gap-1.5">
            <FileText className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
            <div>
              <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                Database Files
              </h4>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                Detailed file-level disk throughput and latency statistics
              </p>
            </div>
          </div>

          {/* Search Input for Files */}
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search file, database, or type..."
              value={tableSearchQuery}
              onChange={(e) => setTableSearchQuery(e.target.value)}
              className="bg-gray-50 dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-7 py-1.5 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-amber-500/50 w-64 h-8"
            />
            {tableSearchQuery && (
              <button
                onClick={() => setTableSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-white"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto overflow-y-auto custom-scroll border border-gray-200 dark:border-gray-700/50 rounded-lg">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-gray-200 dark:border-gray-700/80 bg-gray-50 dark:bg-gray-900 shadow-sm text-[11px] text-gray-600 dark:text-gray-400 uppercase tracking-wider select-none whitespace-nowrap">
                <th className="py-2.5 px-3.5 bg-gray-50 dark:bg-gray-900">File Name</th>
                <th className="py-2.5 px-3.5 bg-gray-50 dark:bg-gray-900">Database</th>
                <th className="py-2.5 px-3.5 bg-gray-50 dark:bg-gray-900">Type</th>
                <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">Current</th>
                <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">Average</th>
                <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">Peak</th>
                <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">Latency (R / W)</th>
                <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">Size (Used / Max)</th>
                <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">Samples</th>
                <th className="py-2.5 px-3.5 text-center bg-gray-50 dark:bg-gray-900">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/30 text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-900/40">
              {paginatedFiles && paginatedFiles.length > 0 ? (
                paginatedFiles.map((f, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
                    <td className="py-2.5 px-3.5 font-bold text-gray-900 dark:text-gray-100 font-mono">
                      {f.file_name}
                    </td>
                    <td className="py-2.5 px-3.5 text-gray-700 dark:text-gray-300">
                      {f.database || 'System / Engine'}
                    </td>
                    <td className="py-2.5 px-3.5">{getTypeBadge(f.type)}</td>
                    <td className="py-2.5 px-3.5 text-right font-mono text-gray-900 dark:text-gray-200">
                      {formatKBps(f.current_kbps)}
                    </td>
                    <td className="py-2.5 px-3.5 text-right font-mono text-gray-700 dark:text-gray-300">
                      {formatKBps(f.average_kbps)}
                    </td>
                    <td className="py-2.5 px-3.5 text-right font-mono text-orange-600 dark:text-orange-400 font-bold">
                      {formatKBps(f.peak_kbps)}
                    </td>
                    <td className="py-2.5 px-3.5 text-right font-mono text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {formatLatency(f.read_latency_ms, f.write_latency_ms)}
                    </td>
                    <td className="py-2.5 px-3.5 text-right font-mono text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {formatFileSizeMB(f.used_mb, f.max_size_mb)}
                    </td>
                    <td className="py-2.5 px-3.5 text-right font-mono text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      {f.samples} / {f.total_samples || labels.length}
                    </td>
                    <td className="py-2.5 px-3.5 text-center">{getStatusBadge(f.status)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="py-10 text-center text-gray-400 dark:text-gray-500 italic">
                    {tableSearchQuery
                      ? `No database files matching "${tableSearchQuery}".`
                      : 'No database files statistics available.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-3">
            <span>
              Showing{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                {filteredFiles.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}
              </span>{' '}
              to{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                {Math.min(currentPage * itemsPerPage, filteredFiles.length)}
              </span>{' '}
              of <span className="font-semibold text-gray-900 dark:text-white">{filteredFiles.length}</span> files
            </span>

            <div className="flex items-center gap-2 border-l border-gray-200 dark:border-gray-800 pl-3">
              <span className="text-[11px] text-gray-500 dark:text-gray-400">Per page:</span>
              <ItemsPerPageDropdown
                value={itemsPerPage}
                onChange={(val: any) => {
                  setItemsPerPage(Number(val));
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-300 cursor-pointer disabled:cursor-not-allowed"
              title="First Page"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-300 cursor-pointer disabled:cursor-not-allowed"
              title="Previous Page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <span className="px-2 py-0.5 text-xs text-gray-700 dark:text-gray-300 font-medium">
              Page <span className="text-gray-900 dark:text-white font-bold">{currentPage}</span> of{' '}
              <span className="text-gray-900 dark:text-white font-bold">{totalPages}</span>
            </span>

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-300 cursor-pointer disabled:cursor-not-allowed"
              title="Next Page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-300 cursor-pointer disabled:cursor-not-allowed"
              title="Last Page"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClickhouseFileIOView;
